import React, { useEffect, useRef, useState } from 'react'
import { Filter, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { FilterOption } from '@/types/history'

/**
 * THE FILTER BAR OF A HISTORY LIST: History's Changes tab and business Activity share it
 * (docs/plans/2026-10-20-change-safety.md §5.17.2, "the same filter bar").
 *
 * It owns no query. Each change reports `{ key: value | null }` to the page, which visits its own
 * URL with the filters as query params (`router.visit` with preserveState and preserveScroll: a
 * filter change on the current page, never a full reload). The server validates every value and
 * echoes back what it applied, so the bar always renders the server's `filters`, never a guess.
 *
 * Selects apply on choice. Dates apply a moment after the last keystroke: a date typed digit by
 * digit passes through several valid dates on the way, and each would otherwise be a visit.
 */
export interface SelectFilter {
  key: string
  label: string
  /** The "no filter" choice: "Everyone", "All areas". */
  allLabel: string
  value: string | null
  options: FilterOption[]
}

interface HistoryFiltersProps {
  /** Prefixes the inputs' ids, so labels stay tied to their own inputs. */
  idPrefix: string
  selects: SelectFilter[]
  from: string | null
  to: string | null
  /** True when any filter is applied: shows "Clear filters". */
  active: boolean
  onChange: (updates: Record<string, string | null>) => void
  onClear: () => void
  className?: string
  style?: React.CSSProperties
}

const ALL = 'all'
const DATE_SETTLE_MS = 400
const LABEL = 'mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'

export default function HistoryFilters({
  idPrefix,
  selects,
  from,
  to,
  active,
  onChange,
  onClear,
  className,
  style,
}: HistoryFiltersProps) {
  const [fromDraft, setFromDraft] = useState(from ?? '')
  const [toDraft, setToDraft] = useState(to ?? '')
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // The settled date reports through the page's latest handler, so a select chosen while a date
  // was settling is not undone by it.
  const report = useRef(onChange)
  report.current = onChange

  // The server's echo is the truth: a clear, a rejected date or a back-button visit lands here.
  useEffect(() => setFromDraft(from ?? ''), [from])
  useEffect(() => setToDraft(to ?? ''), [to])
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
  }, [])

  function changeDate(key: 'from' | 'to', value: string) {
    if (key === 'from') setFromDraft(value)
    else setToDraft(value)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => report.current({ [key]: value || null }), DATE_SETTLE_MS)
  }

  return (
    <div className={className} style={style}>
      <div className="rounded-xl border border-border/60 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Filters</span>
          {active && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-6 rounded-lg px-2 text-xs text-muted-foreground"
              onClick={onClear}
            >
              <X className="mr-1 h-3 w-3" />
              Clear filters
            </Button>
          )}
        </div>

        {/* One row on a desktop, whether the bar has two selects (Changes) or three (Activity): the
            selects share what the dates leave, between a floor that keeps a label readable and a cap
            that stops two of them stretching across the card. A narrower window wraps them. */}
        <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap sm:items-end">
          {selects.map((select) => {
            const id = `${idPrefix}-${select.key}`
            return (
              <div key={select.key} className="col-span-2 min-w-0 sm:min-w-[150px] sm:max-w-[220px] sm:flex-1">
                <Label htmlFor={id} className={LABEL}>
                  {select.label}
                </Label>
                <Select
                  value={select.value ?? ALL}
                  onValueChange={(value) => onChange({ [select.key]: value === ALL ? null : value })}
                >
                  <SelectTrigger id={id} className="cursor-pointer rounded-lg border-border/60" aria-label={select.label}>
                    <SelectValue placeholder={select.allLabel} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL} className="cursor-pointer">
                      {select.allLabel}
                    </SelectItem>
                    {select.options.map((option) => (
                      <SelectItem key={option.value} value={option.value} className="cursor-pointer">
                        <span className="block max-w-[260px] truncate">{option.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )
          })}

          <div className="min-w-0 sm:w-[150px] sm:shrink-0">
            <Label htmlFor={`${idPrefix}-from`} className={LABEL}>
              From
            </Label>
            <Input
              id={`${idPrefix}-from`}
              type="date"
              className="cursor-pointer rounded-lg border-border/60"
              value={fromDraft}
              // The native picker greys out a "from" after the "to": a constraint, not a comparison.
              max={toDraft || undefined}
              onChange={(e) => changeDate('from', e.target.value)}
            />
          </div>
          <div className="min-w-0 sm:w-[150px] sm:shrink-0">
            <Label htmlFor={`${idPrefix}-to`} className={LABEL}>
              To
            </Label>
            <Input
              id={`${idPrefix}-to`}
              type="date"
              className="cursor-pointer rounded-lg border-border/60"
              value={toDraft}
              min={fromDraft || undefined}
              onChange={(e) => changeDate('to', e.target.value)}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
